import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import {
  Button,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Typography,
  InputAdornment,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';
import TagModal from '../../components/TagModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import toastError from '../../errors/toastError';

const useStyles = makeStyles(theme => ({
  page: {
    gap: theme.spacing(2),
  },
  header: {
    alignItems: 'flex-start',
    padding: theme.spacing(1, 0, 2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  toolbar: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flex: 'none',
      width: 'auto',
      justifyContent: 'flex-end',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      justifyContent: 'flex-start',
    },
  },
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1.5),
    overflowX: 'hidden',
    overflowY: 'hidden',
    ...theme.scrollbarStyles,
    borderRadius: 14,
    borderColor: theme.palette.divider,
    background: theme.palette.type === 'dark' ? theme.palette.background.default : '#f4f6fa',
    minHeight: 0,
    minWidth: 0,
  },
  button: {
    borderRadius: 10,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.primary.main,
    boxShadow: 'none',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  filterDialogContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  filterFieldFull: {
    gridColumn: '1 / -1',
  },
  searchInput: {
    minWidth: 220,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: theme.palette.background.paper,
    },
  },
  dateInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: theme.palette.background.paper,
    },
  },
  sortSelect: {
    minWidth: 180,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: theme.palette.background.paper,
    },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [searchParam, setSearchParam] = useState('');
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedLane, setSelectedLane] = useState(null);
  const [deletingLane, setDeletingLane] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      await fetchTickets(fetchedTags);
      return fetchedTags;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      const { data } = await api.get('/ticket/kanban', {
        params: {
          queueIds: JSON.stringify(queueIds),
          startDate: startDate,
          endDate: endDate,
        },
      });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action)) {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
    setFilterModalOpen(false);
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  };

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    const normalizedSearch = searchParam.trim().toLowerCase();
    const searchableTickets = normalizedSearch
      ? fetchedTickets.filter(ticket => {
        const contactName = ticket.contact?.name || '';
        const ticketNumber = String(ticket.id || '');
        const lastMessage = ticket.lastMessage || '';
        return `${contactName} ${ticketNumber} ${lastMessage}`.toLowerCase().includes(normalizedSearch);
      })
      : fetchedTickets;

    const sortedTickets = [...searchableTickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
          tag,
        };
      }),
    ];

    // Aplicar ordem personalizada se existir
    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) {
      organizeLanes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder, laneOrder, loadingLaneOrder, searchParam]);

  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);
    try {
      await api.delete(`/ticket-tags/${ticketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        toast.success('Ticket Tag atualizado com sucesso!');
      } else {
        toast.success('Ticket Tag removido!');
      }

      fetchTickets();
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddColumnClick = () => {
    setSelectedLane(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedLane(null);
    setTagModalOpen(false);
  };

  const handleEditLane = lane => {
    if (!lane?.tag) return;
    setSelectedLane(lane.tag);
    setTagModalOpen(true);
  };

  const handleAskDeleteLane = lane => {
    if (!lane?.tag) return;
    setDeletingLane(lane.tag);
    setConfirmModalOpen(true);
  };

  const handleDeleteLane = async () => {
    if (!deletingLane) return;

    try {
      await api.delete(`/tags/${deletingLane.id}`);
      toast.success(i18n.t("tagsKanban.toasts.deleted"));
      setDeletingLane(null);
      setConfirmModalOpen(false);
      await fetchTags();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Apenas administradores podem reordenar as lanes do Kanban');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      toast.success('Ordem das lanes atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error('Erro ao salvar ordem das lanes');
    }
  };

  return (
    <MainContainer className={classes.page}>
      <ConfirmationModal
        title={deletingLane && `${i18n.t("tagsKanban.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleDeleteLane}
      >
        {i18n.t("tagsKanban.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          tagId={selectedLane && selectedLane.id}
          kanban={1}
          onSaved={fetchTags}
          title={selectedLane ? "Editar coluna" : "Nova coluna"}
          subtitle="Configure uma etapa do seu funil"
          submitLabel={selectedLane ? "Salvar" : "Adicionar"}
        />
      )}
      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Filtros do Kanban</DialogTitle>
        <DialogContent dividers>
          <Box className={classes.filterDialogContent}>
            <TextField
              label="Buscar"
              placeholder="Contato, ticket ou mensagem"
              type="search"
              value={searchParam}
              onChange={event => setSearchParam(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              className={classes.filterFieldFull}
              size="small"
              fullWidth
            />
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterFieldFull}
              fullWidth
            >
              <InputLabel htmlFor="sort-order-select">Ordenar por</InputLabel>
              <Select
                native
                value={sortOrder}
                onChange={handleSortOrderChange}
                label="Ordenar por"
                inputProps={{
                  name: 'sortOrder',
                  id: 'sort-order-select',
                }}
              >
                <option value="ticketNumber">Número do Ticket</option>
                <option value="lastMessageTime">Última Mensagem</option>
                <option value="valorDesc">Valor (maior para menor)</option>
              </Select>
            </FormControl>
            <TextField
              label="Data de início"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              className={classes.dateInput}
              size="small"
              fullWidth
            />
            <TextField
              label="Data de fim"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              className={classes.dateInput}
              size="small"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterModalOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchClick}
            className={classes.button}
          >
            Buscar
          </Button>
        </DialogActions>
      </Dialog>
      <MainHeader className={classes.header}>
        <Box className={classes.titleBlock}>
          <Title>Kanban</Title>
          <Typography className={classes.subtitle}>
            Organize seus atendimentos por etapas
          </Typography>
        </Box>
        <MainHeaderButtonsWrapper className={classes.toolbar}>
          <Tooltip title="Filtrar Kanban">
            <IconButton
              aria-label="Filtrar Kanban"
              onClick={() => setFilterModalOpen(true)}
              className={classes.filterButton}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddColumnClick}
                className={classes.button}
                startIcon={<AddIcon />}
              >
                Nova coluna
              </Button>
            )}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          isAdmin={user.profile === 'admin'}
          onEditLane={handleEditLane}
          onDeleteLane={handleAskDeleteLane}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
